import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '@environments/environment';
import { ApiService } from './api.service';
import { TranslationService } from './translation.service';
import { AuthService } from '@features/auth/services/auth.service';
import { DEFAULT_TERMS } from '../i18n/terms';
import { StoredTranslationTerms, toTermSet } from '../i18n/whitelabel-terms.mapper';
import { CustomDashboardConfig } from '@features/dashboard/models/custom-dashboard.model';

/** Hydrated file reference. `signedUrl` is minted per request and expires in 1h. */
interface WhitelabelFile {
  id: string;
  filename: string;
  mimetype: string;
  signedUrl: string;
}

/**
 * Per-entry sidebar renames. Opaque to the server — the FE owns this shape.
 *
 * Visibility is NOT here: which entries a user sees is decided by their BE-288
 * portalAccess claims, not by whitelabel config.
 */
interface NavigationTerms {
  en?: Record<string, string>;
  ar?: Record<string, string>;
}

export interface BusinessWhitelabel {
  businessId: string;
  whitelabelEnabled: boolean;
  sidebarLogoFileId: string | null;
  sidebarLogoFile: WhitelabelFile | null;
  squareMarkLogoFileId: string | null;
  squareMarkLogoFile: WhitelabelFile | null;
  translationTerms: StoredTranslationTerms | null;
  navigationTerms: NavigationTerms | null;
  /** Deprecated and derived by the BE from `dashboardConfig.source`. Do not read. */
  enableCustomDashboard?: boolean;
  /** Which dashboard to load (FE-240). Always present on a current BE. */
  dashboardConfig?: CustomDashboardConfig;
}

interface MyBusinessResponse {
  id: string;
  whitelabel: BusinessWhitelabel | null;
}

/** Only the one field this service needs off the core profile call. */
interface BusinessUserMe {
  whitelabelEnabled?: boolean;
}

export const WAKI_DEFAULT_LOGO = '/assets/images/waki-logo.svg';
export const WAKI_DEFAULT_MARK = '/assets/images/collapsed_logo.svg';

/**
 * FE-236: applies a client's own vocabulary and branding to the portal shell.
 *
 * Load order, per docs/business-whitelabel-settings-frontend-integration.md:
 *   1. GET /core/business-user/me      → whitelabelEnabled
 *   2. if true, GET /core/business/my-business → logos + terms
 *
 * `/my-business` takes no id: the server resolves the business from the JWT.
 *
 * Branding is cosmetic, so every failure path falls back to Waki defaults rather
 * than blocking the portal. That includes 400 BUSINESS_USER_INVALID_CARDINALITY,
 * which fails closed by design when a user is bound to 0 or 2+ businesses.
 */
@Injectable({ providedIn: 'root' })
export class WhitelabelService {
  private readonly api = inject(ApiService);
  private readonly i18n = inject(TranslationService);
  private readonly auth = inject(AuthService);

  private readonly config = signal<BusinessWhitelabel | null>(null);

  readonly isWhitelabelled = computed(() => this.config()?.whitelabelEnabled === true);

  /**
   * Falls back field by field, not all-or-nothing: a business is often flagged
   * before any logo is uploaded, which is a normal setup state.
   *
   * Reads the *File rather than the *FileId, which also covers the doc's case of
   * a non-null id whose file row is missing — that must read as "no logo".
   */
  readonly sidebarLogo = computed(() =>
    this.config()?.sidebarLogoFile?.signedUrl || WAKI_DEFAULT_LOGO);

  /** Collapsed rail reuses the wide logo when no square mark is set. */
  readonly squareMark = computed(() =>
    this.config()?.squareMarkLogoFile?.signedUrl || this.config()?.sidebarLogoFile?.signedUrl
    || WAKI_DEFAULT_MARK);

  /** FE-240: the business's dashboard is built from its coupons, not payments and orders. */
  readonly hasCustomDashboard = computed(() => this.config()?.dashboardConfig?.source === 'COUPONS');

  /**
   * FE-272: a white-labelled business can switch the Dashboard's Dismissal tab
   * off. Unset means on, and a business that is no longer white-labelled keeps
   * the tab whatever it stored.
   */
  readonly dismissalTabEnabled = computed(() =>
    !this.isWhitelabelled() || this.config()?.dashboardConfig?.dismissalTabEnabled !== false);

  /**
   * True once this session's config has been fetched (or given up on). Lets the
   * dashboard wait for it after a fresh login, where APP_INITIALIZER has not
   * held the shell back, instead of painting the standard page and swapping.
   */
  readonly isSettled = signal(false);

  /** Guards against reloading for a session that is already applied. */
  private loadedFor: string | null = null;

  /**
   * How long bootstrap will wait for branding before giving up and rendering
   * with platform wording. Branding is cosmetic — a slow or dead config call
   * must never leave the portal on a blank screen.
   */
  private static readonly BOOTSTRAP_TIMEOUT_MS = 4000;

  constructor() {
    // Covers every path into a session — login, OTP verify, and the restore from
    // localStorage on refresh — because all three end at this signal.
    effect(() => {
      const user = this.auth.currentUser();
      const userId = user?._id ?? null;

      if (!userId) {
        this.reset();
        return;
      }
      if (this.loadedFor === userId) return;

      this.loadedFor = userId;
      void this.load();
    }, { allowSignalWrites: true });
  }

  /**
   * Resolves once branding is settled, so the shell can be held back until then.
   * Called from APP_INITIALIZER: without it the sidebar paints the platform's own
   * wording first and visibly re-renders into the business's.
   *
   * Only meaningful on a reload, where the session is restored from storage
   * before bootstrap. A fresh login has no session yet, so this returns at once
   * and the effect above applies the branding when the user arrives.
   */
  async ready(): Promise<void> {
    const userId = this.auth.currentUser()?._id ?? null;
    if (!userId || this.loadedFor === userId) return;

    this.loadedFor = userId;

    // Fails open: whichever settles first wins, and load() already swallows its
    // own errors into platform defaults.
    await Promise.race([
      this.load(),
      new Promise<void>(resolve => setTimeout(resolve, WhitelabelService.BOOTSTRAP_TIMEOUT_MS)),
    ]);
  }

  /** Step 2 then step 3 of the documented flow. */
  private async load(): Promise<void> {
    // Feature off: no call at all, platform branding for everyone.
    if (!environment.features.isWhitelabelEnabled) {
      this.applyConfig(null);
      this.isSettled.set(true);
      return;
    }
    try {
      const me = await firstValueFrom(this.api.get<BusinessUserMe>('/business-user/me'));

      // undefined means no business assignment, or not bound to exactly one.
      if (me?.whitelabelEnabled !== true) {
        this.applyConfig(null);
        return;
      }

      const business = await firstValueFrom(
        this.api.get<MyBusinessResponse>('/business/my-business'),
      );
      this.applyConfig(business?.whitelabel ?? null);
    } catch {
      // Any failure renders Waki branding. Branding must never block the portal.
      this.applyConfig(null);
    } finally {
      this.isSettled.set(true);
    }
  }

  /** Also the entry point for admin preview, which applies another business's config. */
  applyConfig(config: BusinessWhitelabel | null): void {
    if (!config?.whitelabelEnabled) {
      this.config.set(null);
      this.i18n.setTerms(DEFAULT_TERMS);
      return;
    }

    this.config.set(config);
    this.i18n.setTerms(toTermSet(config.translationTerms));
  }

  /** Back to platform wording and Waki branding. Fires on logout. */
  reset(): void {
    this.loadedFor = null;
    this.isSettled.set(false);
    this.applyConfig(null);
  }

  /** Per-business rename for one sidebar entry, if the business set one. */
  navLabel(id: string, language: 'en' | 'ar'): string | null {
    return this.config()?.navigationTerms?.[language]?.[id] || null;
  }
}
